import {
   createContext,
   useRef,
   useState,
   SetStateAction,
   Dispatch,
   RefObject,
   PropsWithChildren,
   useContext,
   useCallback,
} from 'react';
import { zoom, ZoomBehavior, zoomIdentity, ZoomTransform } from 'd3-zoom';
import { select } from 'd3-selection';
import { ImageContext } from './imageContext';

const noop = () => {};

const constrainTransform = (
   containerRef: RefObject<HTMLElement | null>,
   d3Transform: ZoomTransform,
   width: number,
   height: number
) => {
   const container = containerRef.current;
   // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
   const containerCenter = { x: container.offsetWidth / 2, y: container.offsetHeight / 2 };
   const image = getImageBounds(d3Transform, width, height);

   let transform = d3Transform;

   if (image.left > containerCenter.x) {
      const x = containerCenter.x;
      const scaledX = x / d3Transform.k - d3Transform.x / d3Transform.k;
      transform = transform.translate(scaledX, 0);
   }

   if (image.right < containerCenter.x) {
      const x = containerCenter.x - image.width;
      const scaledX = x / d3Transform.k - d3Transform.x / d3Transform.k;
      transform = transform.translate(scaledX, 0);
   }

   if (image.top > containerCenter.y) {
      const y = containerCenter.y;
      const scaledY = y / d3Transform.k - d3Transform.y / d3Transform.k;
      transform = transform.translate(0, scaledY);
   }

   if (image.bottom < containerCenter.y) {
      const y = containerCenter.y - image.height;
      const scaledY = y / d3Transform.k - d3Transform.y / d3Transform.k;
      transform = transform.translate(0, scaledY);
   }

   return transform;
};

const getImageBounds = (d3Transform: ZoomTransform, width: number, height: number) => {
   const scale = d3Transform.k;
   const scaledWidth = scale * width;
   const scaledHeight = scale * height;

   const imagebounds = {
      width: scaledWidth,
      height: scaledHeight,
      left: d3Transform.x,
      right: d3Transform.x + scaledWidth,
      top: d3Transform.y,
      bottom: d3Transform.y + scaledHeight,
   };

   return imagebounds;
};

type ZoomListener = (_event: { transform: ZoomTransform }) => void;

const getD3ZoomHandler = (
   setD3Transform: Dispatch<SetStateAction<ZoomTransform>>
): ZoomListener => {
   // @ts-expect-error TS(2322) FIXME: Type 'null' is not assignable to type 'number'.
   let currentAnimationId: number = null;

   const zoomed: ZoomListener = event => {
      if (!event) {
         return;
      }

      const currentTransform = event.transform;

      cancelAnimationFrame(currentAnimationId);

      currentAnimationId = requestAnimationFrame(() => {
         setD3Transform(currentTransform);
      });
   };

   return zoomed;
};

const applyZoom = (
   d3Zoom: ZoomBehavior<Element, unknown>,
   elementRef: RefObject<SVGSVGElement | null>,
   initialTransform: ZoomTransform
) => {
   // Attaches scroll listener
   // See comment below: Let's only attach one handler
   const d3Selection = select(elementRef.current);
   // @ts-expect-error TS(2345) FIXME: Argument of type 'ZoomBehavior<Element, unknown>' ... Remove this comment to see the full error message
   d3Selection.call(d3Zoom);

   // Disable double click zoom
   d3Selection.on('dblclick.zoom', null);

   // Apply initial transform (no animation)
   // @ts-expect-error TS(2345) FIXME: Argument of type 'Selection<SVGSVGElement | null, ... Remove this comment to see the full error message
   d3Zoom.transform(d3Selection, initialTransform);
};

const tweenZoom = (
   elementRef: RefObject<SVGSVGElement | null>,
   d3Zoom: ZoomBehavior<Element, unknown>,
   newTransform: ZoomTransform,
   durationms: number,
   callback: () => void
) => {
   const duration = durationms || 750;
   const d3Selection = select(elementRef.current);
   // transition here is missing? This looks to be a bug due to package upgrades?
   // @ts-expect-error TS(2339) FIXME: Property 'transition' does not exist on type 'Sele... Remove this comment to see the full error message
   const transition = d3Selection.transition().duration(duration).on('end', callback);
   transition.call(d3Zoom.transform, newTransform);
};

const zoomContextDefaults = {
   elementRef: { current: null } as RefObject<SVGSVGElement | null>,
   containerRef: { current: null } as RefObject<HTMLElement | null>,
   d3Transform: zoomIdentity,
   scaleAndCenterOn: noop as (_x: number, _y: number, _k: number, _afterTween?: () => void) => void,
   zoomBy: noop as (_factor: number) => void,
   initZoom: noop as (_initialZoomFactor?: number, _x?: number, _y?: number) => void,
   getInitialZoomFactor: noop as (_initialZoomFactor?: number) => number,
};

const ZoomContext = createContext(zoomContextDefaults);

const getMaxZoomLevel = (
   containerRef: RefObject<HTMLElement | null>,
   width: number,
   height: number
) => getScaleToFitContainer(containerRef, width, height) / 3;

const getScaleToFitContainer = (
   containerRef: RefObject<HTMLElement | null>,
   width: number,
   height: number
) => {
   const container = containerRef.current;
   // @ts-expect-error TS(2531) FIXME: Object is possibly 'null'.
   const containerSize = { width: container.offsetWidth, height: container.offsetHeight };

   const widthScale = containerSize.width / width;
   const heightScale = containerSize.height / height;

   return Math.min(widthScale, heightScale);
};

const getCenteringTranslation = (container: HTMLElement, x: number, y: number, k: number) => {
   const containerCenter = { x: container.offsetWidth / 2, y: container.offsetHeight / 2 };
   const centerPoint = {
      x: containerCenter.x - x,
      y: containerCenter.y - y,
   };
   const translatedToContainerCenter = zoomIdentity
      .translate(centerPoint.x, centerPoint.y)
      .scale(k);
   return translatedToContainerCenter;
};

type RequiredZoomProps = {
   defaultScalingComparedToContainer: number;
   containerRef: RefObject<HTMLElement | null>;
};

function getZoomBehavior(
   containerRef: RefObject<HTMLElement | null>,
   zoomHandler: ZoomListener,
   width: number,
   height: number
) {
   const maxZoomIn = 10;

   if (!containerRef.current) {
      return;
   }

   const maxZoomOut = getMaxZoomLevel(containerRef, width, height);

   const constrain = (d3Transform: ZoomTransform) =>
      constrainTransform(containerRef, d3Transform, width, height);

   const behavior = zoom()
      .scaleExtent([maxZoomOut, maxZoomIn])
      .constrain(constrain)
      .on('zoom', zoomHandler);

   return behavior;
}

function ZoomableDataProvider({
   defaultScalingComparedToContainer,
   containerRef,
   children,
}: PropsWithChildren<RequiredZoomProps>) {
   const elementRef = useRef(null);
   const zoomBehaviorRef = useRef<ZoomBehavior<Element, unknown>>(undefined);

   const [d3Transform, setD3Transform] = useState<ZoomTransform>();
   // @ts-expect-error TS(2345) FIXME: Argument of type 'Dispatch<SetStateAction<ZoomTran... Remove this comment to see the full error message
   const zoomHandler = useCallback(getD3ZoomHandler(setD3Transform), [setD3Transform]);

   const imageContext = useContext(ImageContext);
   const width = imageContext.width;
   const height = imageContext.height;

   const getInitialZoomFactor = useCallback(
      (zoomFactor: number = defaultScalingComparedToContainer) => {
         const fitToContainerScale = containerRef.current
            ? getScaleToFitContainer(containerRef, width, height)
            : 1;
         const initialZoomFactor = fitToContainerScale * zoomFactor;
         return initialZoomFactor;
      },
      [containerRef?.current, width, height, defaultScalingComparedToContainer]
   );

   const initZoom = useCallback(
      (zoomFactor: number = defaultScalingComparedToContainer, x: number, y: number) => {
         const initialZoomFactor = getInitialZoomFactor(zoomFactor);

         const initialX = x || width / 2;
         const initialY = y || height / 2;
         const translatedToContainerCenter = getCenteringTranslation(
            // @ts-expect-error TS(2345) FIXME: Argument of type 'HTMLElement | null' is not assig... Remove this comment to see the full error message
            containerRef.current,
            initialX * initialZoomFactor,
            initialY * initialZoomFactor,
            initialZoomFactor
         );
         const newBehavior = getZoomBehavior(containerRef, zoomHandler, width, height);
         zoomBehaviorRef.current = newBehavior;
         // @ts-expect-error TS(2345) FIXME: Argument of type 'ZoomBehavior<Element, unknown> |... Remove this comment to see the full error message
         applyZoom(newBehavior, elementRef, translatedToContainerCenter);
      },
      [
         width,
         height,
         containerRef,
         zoomHandler,
         defaultScalingComparedToContainer,
         applyZoom,
         getInitialZoomFactor,
      ]
   );

   const zoomBy = useCallback(
      (factor: number) => {
         const d3Selection = select(elementRef.current);
         const duration = 300;
         // transition here is missing? This looks to be a bug due to package upgrades?
         // @ts-expect-error TS(2339) FIXME: Property 'transition' does not exist on type 'Sele... Remove this comment to see the full error message
         const transition = d3Selection.transition().duration(duration);
         // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
         transition.call(zoomBehaviorRef.current.scaleBy, factor);
      },
      [zoomBehaviorRef.current, elementRef.current]
   );

   const scaleAndCenterOn = (x: number, y: number, k: number, afterTween: () => void) => {
      const durationms = 750;
      // @ts-expect-error TS(2345) FIXME: Argument of type 'HTMLElement | null' is not assig... Remove this comment to see the full error message
      const translatedToContainerCenter = getCenteringTranslation(containerRef.current, x, y, k);
      tweenZoom(
         elementRef,
         // @ts-expect-error TS(2345) FIXME: Argument of type 'ZoomBehavior<Element, unknown> |... Remove this comment to see the full error message
         zoomBehaviorRef.current,
         translatedToContainerCenter,
         durationms,
         afterTween
      );
   };

   const zoomContextData = {
      elementRef,
      containerRef,
      d3Transform,
      scaleAndCenterOn,
      zoomBy,
      initZoom,
      getInitialZoomFactor,
   };

   // @ts-expect-error TS(2322) FIXME: Type '{ elementRef: React.MutableRefObject<null>; ... Remove this comment to see the full error message
   return <ZoomContext.Provider value={zoomContextData}>{children}</ZoomContext.Provider>;
}

export { ZoomableDataProvider, ZoomContext };
